/** 收入登記（來源：JHC／Roadshow／B2B／其他；可影相或附相片／PDF） */
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { api, todayISO, type Meta } from '../lib/api';
import ProofAttach, { type Proof } from '../components/ProofAttach';

export default function Income({ meta, onDone }: { meta: Meta; onDone: () => void }) {
  const { t, i18n } = useTranslation();
  const zh = String(i18n.resolvedLanguage ?? 'zh-HK').startsWith('zh');
  const [entityId, setEntityId] = useState(meta.entities[0]?.id ?? '');
  const [txnDate, setTxnDate] = useState(todayISO());
  const [amount, setAmount] = useState('');
  const [incomeCode, setIncomeCode] = useState('');
  const [accountId, setAccountId] = useState('');
  const [customer, setCustomer] = useState('');
  const [docNo, setDocNo] = useState('');
  const [purpose, setPurpose] = useState('');
  const [projectId, setProjectId] = useState('');
  const [proofs, setProofs] = useState<Proof[]>([]);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [saved, setSaved] = useState<string | null>(null);

  const accounts = meta.accounts.filter((a) => a.entity_id === entityId);

  const save = async () => {
    setBusy(true); setErr(null);
    try {
      const r = await api.createVoucher({
        kind: 'receipt',
        entityId, txnDate, amount, incomeCode, accountId,
        paidBy: 'company', paymentMethod: 'bank_transfer',
        projectId: projectId || undefined,
        docType: 'invoice', docNo: docNo || undefined,
        // 客戶：即場新建／同名重用（P1-5）；來源由收入科目自動判斷
        customerName: customer || undefined,
        purpose,
        attachmentIds: proofs.map((p) => p.id),
      });
      setSaved(r.voucherNo);
      setAmount(''); setPurpose(''); setCustomer(''); setDocNo(''); setProofs([]);
    } catch (e) { setErr(e instanceof Error ? e.message : String(e)); } finally { setBusy(false); }
  };

  return (
    <main className="page">
      <h2>{t('income.heading')}</h2>
      {saved && <p className="ok">{t('capture.saved', { no: saved })} <button type="button" onClick={onDone}>{t('home.heading')}</button></p>}
      <section className="card">
        <div className="fields">
          <label className="field">
            <span>{t('expense.amount')} *</span>
            <input inputMode="decimal" className="amount" value={amount} onChange={(e) => setAmount(e.target.value)} placeholder="0.00" />
          </label>
          <label className="field">
            <span>{t('expense.date')} *</span>
            <input type="date" value={txnDate} onChange={(e) => setTxnDate(e.target.value)} />
          </label>
          <label className="field">
            <span>{t('income.category')} *</span>
            <select value={incomeCode} onChange={(e) => setIncomeCode(e.target.value)}>
              <option value="">—</option>
              {meta.incomeCategories.map((c) => <option key={c.code} value={c.code}>{zh ? c.name_zh : c.name_en}</option>)}
            </select>
          </label>
          <label className="field">
            <span>{t('income.account')} *</span>
            <select value={accountId} onChange={(e) => setAccountId(e.target.value)}>
              <option value="">—</option>
              {accounts.map((a) => <option key={a.id} value={a.id}>{a.name}</option>)}
            </select>
          </label>
          <label className="field">
            <span>{t('income.entity')} *</span>
            <select value={entityId} onChange={(e) => { setEntityId(e.target.value); setAccountId(''); }}>
              {meta.entities.map((e) => <option key={e.id} value={e.id}>{e.name_zh}</option>)}
            </select>
          </label>
          <label className="field">
            <span>{t('income.customer')}</span>
            <input
              list="customers-list" value={customer} onChange={(e) => setCustomer(e.target.value)}
              placeholder={t('income.customer_hint')}
            />
            <datalist id="customers-list">
              {meta.customers.map((c) => <option key={c.id} value={c.name} />)}
            </datalist>
          </label>
          <label className="field">
            <span>{t('income.doc_no')}</span>
            <input value={docNo} onChange={(e) => setDocNo(e.target.value)} />
          </label>
          <label className="field">
            <span>{t('expense.purpose')} *</span>
            <input value={purpose} onChange={(e) => setPurpose(e.target.value)} required />
          </label>
          <label className="field">
            <span>{t('expense.project')}</span>
            <select value={projectId} onChange={(e) => setProjectId(e.target.value)}>
              <option value="">—</option>
              {meta.projects.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
            </select>
          </label>
        </div>
        <ProofAttach proofs={proofs} onChange={setProofs} disabled={busy} />
        {err && <p className="err">{err}</p>}
        <button className="cta" onClick={() => void save()} disabled={busy || !amount || !incomeCode || !accountId || !purpose}>
          {t('income.save')}
        </button>
      </section>
    </main>
  );
}
